/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

export interface WalkthroughStep {
  index?: number;
  title?: string;
  markdown: string;
  id?: string;
  minor?: boolean
}

export class AnnotationParser {

  private static TITLE_REGEX: RegExp = /#\s+(.*)/i;
  private static METADATA_REGEX: RegExp = /^\s*(?:\[_metadata_:.*\]:-\s*".*")\s*$/i;
  private static INDEX_REGEX: RegExp = /\[_metadata_:index\]:-\s*"([0-9]+)"/i;
  private static MINOR_REGEX: RegExp = /\[_metadata_:minor\]:-\s*"(true|false)"/i;
  private static ANNOTATION_REGEX: RegExp = /\[_metadata_:annotation\]:-\s*"(.*)"/i;

  public static deserialize(markdown: string, sandboxName?: string,) {
    const result: WalkthroughStep[] = [AnnotationParser.createInitialStep(sandboxName)];

    let metadataStack: string[] = [];
    let markdownStack: string[] = [];

    const splitMarkdown = markdown.split("\n");
    splitMarkdown.forEach((line) => {
      if (AnnotationParser.TITLE_REGEX.test(line)) {
        if (metadataStack.length > 0) {
          result.push(AnnotationParser.deserializeStep(metadataStack, markdownStack));
          metadataStack = [];
          markdownStack = [];
        }
        metadataStack.push(line);
      } else if (AnnotationParser.METADATA_REGEX.test(line)) {
        metadataStack.push(line);
      } else {
        markdownStack.push(line);
      }
    });

    if (metadataStack.length > 0) {
      result.push(AnnotationParser.deserializeStep(metadataStack, markdownStack));
    }

    return result;
  }

  private static deserializeStep(metadata: string[], markdown: string[]) {
    let id: string | undefined;
    let index: number | undefined;
    let title: string | undefined;
    let minor = false;

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < metadata.length; i++) {
      let match = metadata[i].match(AnnotationParser.ANNOTATION_REGEX);
      if (match && match[1]) {
        const parsed = match[1].trim();
        id = parsed !== "NONE" ? parsed : undefined;
      }
      match = metadata[i].match(AnnotationParser.INDEX_REGEX);
      if (match && match[1]) {
        index = parseInt(match[1].trim(), 10);
      }
      match = metadata[i].match(AnnotationParser.TITLE_REGEX);
      if (match && match[1]) {
        title = match[1].trim();
      }
      match = metadata[i].match(AnnotationParser.MINOR_REGEX);
      if (match && match[1]) {
        minor = Boolean(match[1].trim());
      }
    }

    const step = {
      id,
      title,
      index,
      minor,
      markdown: markdown.join("\n").trim(),
    };

    return step;
  }

  private static createInitialStep = (sandboxName?: string) => {
    const markdown = [
      sandboxName ? `This panel will give you a guided tour of the **${sandboxName}** code sample.` : `This panel will give you a guided tour of this code sample.`,
      "",
      "Please use the ⯈ button below to start the tour. Or you can browse through and jump directly to any step using the control above. During the tour, the middle navigation button will recenter the code editor.",
      "",
    ];

    const initialStep: WalkthroughStep = {
      index: 0,
      title: "Getting Started",
      markdown: markdown.join("\n"),
    };

    return initialStep;
  };
}
